// The three headline numbers both money screens lead with, over one date range.
// Shared so the Dashboard and the Expenses page can never disagree about what
// "Saving" means - they render the same figure with different date ranges.

// Types that represent money leaving the account. Income is money in, and Saving is
// money set aside rather than spent, so neither counts as an outflow budget.
const OUTFLOW_BUDGET_TYPES = ['Bill', 'Expense', 'Debt'];

// Actual spend/income per budget row, computed live from real transactions in the
// range. Saving is the one exception: its "actual" tracks linked-goal progress (set
// directly on the budget by GoalDashboard), not day-to-day transactions, so it keeps
// reading actualAmount.
export function makeActualFor(periodExpenses) {
  return (budget) => {
    const type = budget.type || 'Expense';
    if (type === 'Saving') return Number(budget.actualAmount) || 0;
    const kind = type === 'Income' ? 'income' : 'expense';
    return periodExpenses
      .filter((expense) => expense.category === budget.category && (expense.kind || 'expense') === kind)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };
}

// `budgets` should already be narrowed to the ones that apply to this range (recurring
// ones plus any pinned to its month) - the caller knows that rule, this doesn't.
export function summariseMoney({ expenses, budgets, start, end }) {
  const periodExpenses = expenses.filter((item) => item.date >= start && item.date <= end);
  const actualFor = makeActualFor(periodExpenses);

  // Deliberately every transaction in the range, not just the ones whose category has
  // a matching budget row: "money in" and "money out" should account for everything
  // that actually moved, including one-off categories the user never budgeted for.
  const income = periodExpenses
    .filter((item) => (item.kind || 'expense') === 'income')
    .reduce((sum, item) => sum + item.amount, 0);
  const spending = periodExpenses
    .filter((item) => (item.kind || 'expense') !== 'income')
    .reduce((sum, item) => sum + item.amount, 0);

  // Saving is what the user has actually put aside plus what they budgeted to spend
  // and didn't - money still in hand at the end of the range. Overspending on one
  // budget shouldn't create "negative savings", so the unspent half floors at zero.
  const setAside = budgets
    .filter((item) => (item.type || 'Expense') === 'Saving')
    .reduce((sum, item) => sum + actualFor(item), 0);
  const outflowBudgets = budgets.filter((item) => OUTFLOW_BUDGET_TYPES.includes(item.type || 'Expense'));
  const budgeted = outflowBudgets.reduce((sum, item) => sum + item.amount, 0);
  const spentAgainstBudgets = outflowBudgets.reduce((sum, item) => sum + actualFor(item), 0);
  const unspent = Math.max(0, budgeted - spentAgainstBudgets);

  return {
    income,
    spending,
    setAside,
    unspent,
    saving: setAside + unspent,
    budgeted,
    spentAgainstBudgets,
    // Signed, unlike `unspent` - the ring on the Expenses page needs to show overspend.
    leftToSpend: budgeted - spentAgainstBudgets,
    actualFor,
  };
}
